// ============================================================
// Platform Control · Database — operator view of schema health,
// table groups, and runtime readiness. Not a DBA console.
// ============================================================

import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import ViewShell, { Card, Row, Section, ActionButton, ActionRow } from './_ViewShell'
import { SHELL_TOKENS, text } from '../../../shell/styles'
import { api, isAuthError } from '../../../api/client'

interface DbStatus {
  migration_files: number
  critical_tables_checked: number
  critical_tables_present: number
  missing_tables: string[]
}

export default function DatabaseView() {
  const navigate = useNavigate()
  const [status, setStatus] = useState<DbStatus | null>(null)
  const [loading, setLoading] = useState(false)
  const [checked, setChecked] = useState(false)

  const checkDatabase = useCallback(async () => {
    setLoading(true)
    try {
      const r = await api.admin.databaseStatus()
      setStatus(r.database)
      setChecked(true)
    } catch (err) {
      if (!isAuthError(err)) console.warn('[DatabaseView]', err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void checkDatabase() }, [checkDatabase])

  const tablesOk = checked && status && status.missing_tables.length === 0
  const migrations = status?.migration_files ?? 22

  return (
    <ViewShell
      kicker="PLATFORM CONTROL · DATABASE"
      title="Database"
      description="Schema health, table verification, and runtime readiness."
    >
      {/* Status tiles */}
      <Section label="STATUS">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 10 }}>
          <StatusTile label="Migrations" value={String(migrations)} tone={SHELL_TOKENS.brand} />
          <StatusTile
            label="Tables"
            value={checked ? `${status?.critical_tables_present ?? 0}/${status?.critical_tables_checked ?? 0}` : '...'}
            tone={tablesOk ? SHELL_TOKENS.green : checked ? SHELL_TOKENS.amber : undefined}
          />
          <StatusTile label="RLS" value="100%" tone={SHELL_TOKENS.green} />
          <StatusTile label="Provider" value="Supabase" />
        </div>

        {checked && status && status.missing_tables.length > 0 && (
          <div style={{
            marginTop: 12,
            padding: '12px 16px',
            borderRadius: 10,
            background: 'rgba(220,38,38,0.06)',
            border: '1px solid rgba(220,38,38,0.2)',
          }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#f87171', marginBottom: 6 }}>
              {status.missing_tables.length} table{status.missing_tables.length > 1 ? 's' : ''} missing
            </div>
            <div style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: SHELL_TOKENS.inkDim }}>
              {status.missing_tables.join(', ')}
            </div>
          </div>
        )}

        {checked && tablesOk && (
          <div style={{
            marginTop: 12,
            padding: '12px 16px',
            borderRadius: 10,
            background: 'rgba(5,150,105,0.06)',
            border: '1px solid rgba(5,150,105,0.2)',
          }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#34d399' }}>
              All critical tables present and accessible
            </div>
          </div>
        )}
      </Section>

      {/* Table groups */}
      <Section label="TABLE GROUPS">
        <Card>
          <Row label="OLIVIUM trust layer" value="22 tables" hint="olv_* — audit, sessions, keys, seals, alerts, roles" />
          <Row label="Square Code substrate" value="25+ tables" hint="sq_* — projects, workspaces, sites, commands, hosting" />
          <Row label="Workflow + Slack engine" value="6 tables" hint="sc_* — workflows, executions, step runs, threads, approvals" />
        </Card>
      </Section>

      {/* Operator actions */}
      <Section label="ACTIONS">
        <ActionRow>
          <ActionButton variant="primary" onClick={checkDatabase} disabled={loading}>
            {loading ? 'Checking...' : 'Verify All Tables'}
          </ActionButton>
          <ActionButton onClick={() => navigate('/square/control/storage')}>
            Check Storage
          </ActionButton>
          <ActionButton onClick={() => navigate('/square/control/security')}>
            Security Posture
          </ActionButton>
          <ActionButton onClick={() => navigate('/square/olivium/feed')}>
            View Audit Trail
          </ActionButton>
        </ActionRow>
      </Section>

      {/* Connection info */}
      <Section label="CONNECTION">
        <Card>
          <Row label="Provider" value="Supabase Postgres" />
          <Row label="RLS" value="Enforced on all tables" hint="olv_*, sq_*, sc_* — no exceptions" />
          <Row label="Realtime" value="12 published tables" hint="executions, deployments, domains, commands, audit" />
        </Card>
      </Section>
    </ViewShell>
  )
}

function StatusTile({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div style={{
      padding: '16px 18px',
      borderRadius: 12,
      background: SHELL_TOKENS.surface,
      border: `1px solid ${tone ? `${tone}30` : SHELL_TOKENS.border}`,
    }}>
      <div style={{
        ...text.kicker, fontSize: 9, letterSpacing: '0.18em',
        color: SHELL_TOKENS.inkFaint, marginBottom: 8,
      }}>
        {label}
      </div>
      <div style={{
        fontSize: 28, fontWeight: 700,
        color: tone || SHELL_TOKENS.ink,
        fontFamily: 'var(--font-mono)',
      }}>
        {value}
      </div>
    </div>
  )
}
